import { z } from 'zod';

export const ACCEPTED_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.oasis.opendocument.spreadsheet',
  'text/csv',
  'text/tab-separated-values',
] as const;

export const ACCEPTED_DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.xlsx',
  '.pptx',
  '.xls',
  '.ods',
  '.csv',
  '.tsv',
] as const;

export type AcceptedDocumentMimeType = (typeof ACCEPTED_DOCUMENT_MIME_TYPES)[number];

const isoDateTimeSchema = z.string().datetime({
  error: 'La date doit être au format ISO 8601.',
});

export const documentStatusEnum = z.enum(['pending', 'uploading', 'indexing', 'completed', 'failed'], {
  error: 'Le statut documentaire est invalide.',
});

export const documentOriginSchema = z.enum(['manual', 'm365_sharepoint'], {
  error: 'L’origine documentaire est invalide.',
});

export const uploadDocumentSchema = z.object({
  originalFilename: z.string().trim().min(1, {
    error: 'Le nom de fichier d origine est requis.',
  }).max(500, {
    error: 'Le nom de fichier d origine est trop long.',
  }),
});

export const documentSchema = z.object({
  createdAt: isoDateTimeSchema,
  hasSourceFile: z.boolean(),
  id: z.uuid({
    error: 'L identifiant du document est invalide.',
  }),
  mimeType: z.string().trim().min(1),
  name: z.string().trim().min(1).max(255),
  origin: documentOriginSchema,
  originalFilename: z.string().trim().min(1).max(500),
  pageCount: z.number().int().positive().nullable(),
  sizeBytes: z.number().int().positive(),
  status: documentStatusEnum,
  statusMessage: z.string().nullable(),
  updatedAt: isoDateTimeSchema,
  uploadedById: z.string().trim().min(1).nullable(),
  uploadedByEmail: z.string().trim().email().nullable(),
  uploadedByName: z.string().trim().min(1).nullable(),
});

export const documentContentRangeSchema = z.string({
  error: 'La plage de contenu documentaire est invalide.',
}).trim().max(64, {
  error: 'La plage de contenu documentaire est trop longue.',
}).regex(/^bytes=(?:\d+-\d*|-\d+)$/, {
  error: 'Une seule plage HTTP en octets est acceptée.',
}).refine((value) => {
  const match = /^bytes=(\d+)-(\d+)$/.exec(value);
  return !match || Number(match[1]) <= Number(match[2]);
}, {
  error: 'Le début de la plage doit précéder sa fin.',
});

export const documentFiltersSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
  origin: documentOriginSchema.optional(),
  search: z.string().trim().max(120).optional().transform((value) => value || undefined),
  status: documentStatusEnum.optional(),
});

export type UploadDocumentDTO = z.infer<typeof uploadDocumentSchema>;
export type DocumentDTO = z.infer<typeof documentSchema>;
export type DocumentOrigin = z.infer<typeof documentOriginSchema>;
export type DocumentContentRangeDTO = z.infer<typeof documentContentRangeSchema>;
export type DocumentFiltersDTO = z.infer<typeof documentFiltersSchema>;
